import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Divider } from 'primereact/divider';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { formatPrice } from '../../services/giftService';
import '../../css/gift-success.css';

interface GiftPurchase {
  id: string;
  giftType: string;
  recipientEmail: string | null;
  recipientName: string | null;
  status: 'applied' | 'pending';
  totalAmount: number;
  createdAt: Date;
}

const GIFT_TYPE_LABELS: Record<string, string> = {
  'subscription_monthly': '1 Month Premium Subscription',
  'subscription_annual': '1 Year Premium Subscription',
  'customer_subscription_annual': '1 Year Premium Subscription',
  'assessment_single': 'Refinance Assessment',
};

const GiftSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [purchase, setPurchase] = useState<GiftPurchase | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [signedOut, setSignedOut] = useState(false);

  useEffect(() => {
    const loadPurchaseDetails = async () => {
      try {
        setLoading(true);
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');

        if (!sessionId) {
          setError('No session ID found');
          setLoading(false);
          return;
        }

        if (!currentUser?.uid) {
          setError('Not authenticated');
          setLoading(false);
          return;
        }

        // Query the gift purchase by Stripe session ID and agent ID (required by security rules)
        const purchasesQuery = query(
          collection(db, 'agent_gift_purchases'),
          where('agentId', '==', currentUser.uid),
          where('stripeSessionId', '==', sessionId)
        );
        const snapshot = await getDocs(purchasesQuery);

        if (snapshot.empty) {
          // Purchase may still be processing via webhook
          setError('Your purchase is being processed. Please wait a moment and refresh the page.');
          setLoading(false);
          return;
        }

        const doc = snapshot.docs[0];
        if (!doc) {
          setError('Purchase record not found');
          setLoading(false);
          return;
        }
        const data = doc.data();
        setPurchase({
          id: doc.id,
          giftType: data.giftType,
          recipientEmail: data.recipientEmail || null,
          recipientName: data.recipientName || null,
          status: data.status,
          totalAmount: data.totalAmount,
          createdAt: data.createdAt?.toDate?.() || new Date(),
        });
        setLoading(false);
      } catch (err) {
        console.error('Error loading purchase details:', err);
        setError('Failed to load purchase details');
        setLoading(false);
      }
    };

    if (currentUser) {
      setSignedOut(false);
      loadPurchaseDetails();
    } else if (!authLoading) {
      // Auth has settled and there is still no user, so this is terminal - there
      // is no session to look the purchase up with. While auth is still settling
      // we keep the spinner rather than flashing the signed-out state.
      setSignedOut(true);
      setLoading(false);
    }
  }, [currentUser, authLoading]);

  const getGiftTypeLabel = (giftType: string): string => {
    return GIFT_TYPE_LABELS[giftType] || giftType;
  };

  if (signedOut) {
    return (
      <div className="gift-success-page">
        <Card className="success-card">
          <Message
            severity="warn"
            text="You are signed out, so we cannot look up your purchase. Your purchase is safe - sign in to view the details."
            style={{ width: '100%', marginBottom: '1rem' }}
          />
          <div className="action-buttons">
            <Button
              label="Sign In"
              icon="pi pi-sign-in"
              onClick={() => navigate('/agent/login')}
              className="p-button-primary"
            />
            <Button
              label="Back to Dashboard"
              icon="pi pi-home"
              onClick={() => navigate('/agent/dashboard')}
              className="p-button-secondary"
            />
          </div>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="gift-success-page">
        <div className="loading-container">
          <ProgressSpinner />
          <p>Loading your purchase details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gift-success-page">
        <Card className="success-card">
          <Message severity="warn" text={error} style={{ width: '100%', marginBottom: '1rem' }} />
          <div className="action-buttons">
            <Button
              label="Refresh"
              icon="pi pi-refresh"
              onClick={() => window.location.reload()}
              className="p-button-primary"
            />
            <Button
              label="Back to Gifts"
              icon="pi pi-arrow-left"
              onClick={() => navigate('/agent/gifts')}
              className="p-button-secondary"
            />
          </div>
        </Card>
      </div>
    );
  }

  if (!purchase) {
    return (
      <div className="gift-success-page">
        <Message severity="error" text="Purchase not found" />
        <Button
          label="Back to Gift Purchase"
          icon="pi pi-arrow-left"
          onClick={() => navigate('/agent/gifts')}
          style={{ marginTop: '1rem' }}
        />
      </div>
    );
  }

  return (
    <div className="gift-success-page">
      <Card className="success-card">
        <div className="success-header">
          <div className="success-icon">
            <i className="pi pi-check-circle" />
          </div>
          <h1>Purchase Successful!</h1>
          <p>
            {purchase.status === 'applied'
              ? 'The gift has been applied to your customer\'s account'
              : 'The gift will be applied when your customer creates their account'}
          </p>
        </div>

        <Divider />

        <div className="purchase-details">
          <h2>Purchase Details</h2>

          <div className="details-grid">
            <div className="detail-item">
              <span className="detail-label">Gift Type</span>
              <span className="detail-value">{getGiftTypeLabel(purchase.giftType)}</span>
            </div>

            <div className="detail-item">
              <span className="detail-label">Amount</span>
              <span className="detail-value">{formatPrice(purchase.totalAmount)}</span>
            </div>

            {purchase.recipientName && (
              <div className="detail-item">
                <span className="detail-label">Recipient</span>
                <span className="detail-value">{purchase.recipientName}</span>
              </div>
            )}

            {purchase.recipientEmail && (
              <div className="detail-item">
                <span className="detail-label">Email</span>
                <span className="detail-value">{purchase.recipientEmail}</span>
              </div>
            )}

            <div className="detail-item">
              <span className="detail-label">Status</span>
              <span className={`status-badge status-${purchase.status}`}>
                {purchase.status === 'applied' ? 'Applied' : 'Pending'}
              </span>
            </div>
          </div>
        </div>

        <Divider />

        <div className="next-steps">
          <h3>What Happens Next</h3>
          <div className="steps-grid">
            {purchase.status === 'applied' ? (
              <>
                <div className="step-card">
                  <i className="pi pi-check-circle step-icon" style={{ color: '#22c55e' }} />
                  <h4>Gift Applied</h4>
                  <p>The gift has been automatically added to your customer's account</p>
                </div>
                <div className="step-card">
                  <i className="pi pi-bell step-icon" />
                  <h4>Customer Notified</h4>
                  <p>Your customer will see a notification in their EquityWorkx app about the gift</p>
                </div>
                <div className="step-card">
                  <i className="pi pi-star step-icon" />
                  <h4>Ready to Use</h4>
                  <p>Your customer can start using their premium features immediately</p>
                </div>
              </>
            ) : (
              <>
                <div className="step-card">
                  <i className="pi pi-clock step-icon" style={{ color: '#f59e0b' }} />
                  <h4>Awaiting Signup</h4>
                  <p>The gift is saved and waiting for {purchase.recipientEmail || 'your customer'} to create an account</p>
                </div>
                <div className="step-card">
                  <i className="pi pi-user-plus step-icon" />
                  <h4>Auto-Applied on Signup</h4>
                  <p>When they sign up with this email, the gift will be automatically applied</p>
                </div>
                <div className="step-card">
                  <i className="pi pi-send step-icon" />
                  <h4>Invite Your Customer</h4>
                  <p>Let your customer know they have a gift waiting - just download the app and sign up!</p>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="action-buttons">
          <Button
            label="Purchase Another Gift"
            icon="pi pi-gift"
            onClick={() => navigate('/agent/gifts')}
            className="p-button-success"
          />
          <Button
            label="Back to Dashboard"
            icon="pi pi-home"
            onClick={() => navigate('/agent/dashboard')}
            className="p-button-secondary"
          />
        </div>
      </Card>
    </div>
  );
};

export default GiftSuccessPage;
